import { OwnedActionsDto } from '@issuemanager/api-client';
import {
  DEFAULT_FILTER_MODEL_STATE,
  useActionStatuses,
  useDataGridContext,
} from '@issuemanager/apps/client/components';
import { useUserContext } from '@issuemanager/apps/client/contexts';
import { useDateTimeFormat } from '@issuemanager/apps/client/hooks';
import { OwnerColumnFilter } from '@issuemanager/client/features/issues';
import {
  ActionStatus,
  ISSUE_FIELD_ACTION_DUE_DATE,
  ISSUE_FIELD_ACTION_OWNER,
  ISSUE_FIELD_ACTION_STATUS,
  ISSUE_FIELD_DUE_DATE,
  ISSUE_FIELD_ISSUE_STATUS,
  ISSUE_FIELD_OWNER,
  OwnedActionsTypeEnum,
} from '@issuemanager/constants';
import {
  OwnedActionsEnum,
  QuickFilterAllEnum,
} from '@issuemanager/test/constants';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { QuickFilterContainer } from './quick-filter-container';
import { QuickFilterItem } from './quick-filter-item';

export function OwnedActions({
  ownedActions,
  activeFilter,
  setActiveFilter,
}: {
  ownedActions: OwnedActionsDto | undefined;
  activeFilter: QuickFilterAllEnum | null;
  setActiveFilter: (value: QuickFilterAllEnum) => void;
}): React.JSX.Element {
  const context = useDataGridContext();
  const user = useUserContext();
  const { t } = useTranslation('issue');
  const actionStatuses = useActionStatuses();
  const { timezoneOffset } = useDateTimeFormat();

  const userDataForFiltering = {
    ...user.data,
    group: OwnerColumnFilter.ACTION_OWNER,
  };

  const clearFilters = () => {
    context.setFilterModel(DEFAULT_FILTER_MODEL_STATE);
  };
  const actionOpenStatus = useMemo(
    () => actionStatuses.find((s) => s.value === ActionStatus.OPEN),
    [actionStatuses],
  );
  const actionInProgressStatus = useMemo(
    () => actionStatuses.find((s) => s.value === ActionStatus.IN_PROGRESS),
    [actionStatuses],
  );
  const actionToBeApprovedStatus = useMemo(
    () => actionStatuses.find((s) => s.value === ActionStatus.TO_BE_APPROVED),
    [actionStatuses],
  );
  const actionCompletedStatus = useMemo(
    () => actionStatuses.find((s) => s.value === ActionStatus.COMPLETED),
    [actionStatuses],
  );
  const actionPendingStatus = useMemo(
    () => actionStatuses.find((s) => s.value === ActionStatus.PENDING),
    [actionStatuses],
  );

  const handleFilterSelection = (type: string) => {
    clearFilters();
    switch (type) {
      case OwnedActionsTypeEnum.ALL:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          [ISSUE_FIELD_OWNER]: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            [ISSUE_FIELD_ACTION_OWNER]: [userDataForFiltering],
          }),
          'quick-filter',
        );
        break;
      case OwnedActionsTypeEnum.OPEN:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          [ISSUE_FIELD_OWNER]: true,
          [ISSUE_FIELD_ISSUE_STATUS]: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            [ISSUE_FIELD_ACTION_OWNER]: [userDataForFiltering],
            [ISSUE_FIELD_ACTION_STATUS]: [actionOpenStatus],
          }),
          'quick-filter',
        );
        break;
      case OwnedActionsTypeEnum.IN_PROGRESS:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          [ISSUE_FIELD_OWNER]: true,
          [ISSUE_FIELD_ISSUE_STATUS]: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            [ISSUE_FIELD_ACTION_OWNER]: [userDataForFiltering],
            [ISSUE_FIELD_ACTION_STATUS]: [actionInProgressStatus],
          }),
          'quick-filter',
        );
        break;
      case OwnedActionsTypeEnum.TO_BE_APPROVED:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          [ISSUE_FIELD_OWNER]: true,
          [ISSUE_FIELD_ISSUE_STATUS]: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            [ISSUE_FIELD_ACTION_OWNER]: [userDataForFiltering],
            [ISSUE_FIELD_ACTION_STATUS]: [actionToBeApprovedStatus],
          }),
          'quick-filter',
        );
        break;
      case OwnedActionsTypeEnum.OVERDUE:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          [ISSUE_FIELD_OWNER]: true,
          [ISSUE_FIELD_DUE_DATE]: true,
          [ISSUE_FIELD_ISSUE_STATUS]: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            [ISSUE_FIELD_ACTION_OWNER]: [userDataForFiltering],
            [ISSUE_FIELD_ACTION_DUE_DATE]: [
              null,
              dayjs().utc().utcOffset(timezoneOffset),
            ],
            [ISSUE_FIELD_ACTION_STATUS]: [
              actionOpenStatus,
              actionInProgressStatus,
              actionToBeApprovedStatus,
              actionPendingStatus,
            ],
          }),
          'quick-filter',
        );
        break;
      case OwnedActionsTypeEnum.COMPLETED:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          [ISSUE_FIELD_OWNER]: true,
          [ISSUE_FIELD_ISSUE_STATUS]: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            [ISSUE_FIELD_ACTION_OWNER]: [userDataForFiltering],
            [ISSUE_FIELD_ACTION_STATUS]: [actionCompletedStatus],
          }),
          'quick-filter',
        );
        break;
    }
  };

  return (
    <QuickFilterContainer label={t('quickFilters.ownedActions.title')}>
      <QuickFilterItem
        label={t('quickFilters.ownedActions.all')}
        number={ownedActions?.all}
        onClick={() => {
          handleFilterSelection(OwnedActionsTypeEnum.ALL);
          setActiveFilter(OwnedActionsEnum.ALL);
        }}
        isActive={activeFilter === OwnedActionsEnum.ALL}
      />
      <QuickFilterItem
        label={t('quickFilters.ownedActions.open')}
        number={ownedActions?.open}
        onClick={() => {
          handleFilterSelection(OwnedActionsTypeEnum.OPEN);
          setActiveFilter(OwnedActionsEnum.OPEN);
        }}
        isActive={activeFilter === OwnedActionsEnum.OPEN}
      />
      <QuickFilterItem
        label={t('quickFilters.ownedActions.inProgress')}
        number={ownedActions?.inProgress}
        onClick={() => {
          handleFilterSelection(OwnedActionsTypeEnum.IN_PROGRESS);
          setActiveFilter(OwnedActionsEnum.IN_PROGRESS);
        }}
        isActive={activeFilter === OwnedActionsEnum.IN_PROGRESS}
      />
      <QuickFilterItem
        label={t('quickFilters.ownedActions.toBeApproved')}
        number={ownedActions?.toBeApproved}
        onClick={() => {
          handleFilterSelection(OwnedActionsTypeEnum.TO_BE_APPROVED);
          setActiveFilter(OwnedActionsEnum.TO_BE_APPROVED);
        }}
        isActive={activeFilter === OwnedActionsEnum.TO_BE_APPROVED}
      />
      <QuickFilterItem
        label={t('quickFilters.ownedActions.overdue')}
        number={ownedActions?.overdue}
        onClick={() => {
          handleFilterSelection(OwnedActionsTypeEnum.OVERDUE);
          setActiveFilter(OwnedActionsEnum.OVERDUE);
        }}
        isActive={activeFilter === OwnedActionsEnum.OVERDUE}
      />
      <QuickFilterItem
        label={t('quickFilters.ownedActions.completed')}
        number={ownedActions?.completed}
        onClick={() => {
          handleFilterSelection(OwnedActionsTypeEnum.COMPLETED);
          setActiveFilter(OwnedActionsEnum.COMPLETED);
        }}
        isActive={activeFilter === OwnedActionsEnum.COMPLETED}
      />
    </QuickFilterContainer>
  );
}
